import {
  useCallback, useEffect, useRef, useState
} from 'react'
import {
  Loader2, Sparkles
} from 'lucide-react'
import {
  integrationSuggestionsService,
  isInlineConnectable,
} from '../../services/api'
import type {
  IntegrationSuggestionItem
} from '../../services/api'
import {
  SuggestedIntegrationTile
} from '../SuggestedIntegrationTile'
import {
  useSuggestionConnect, verdictFromShortlist
} from '../../hooks/useSuggestionConnect'
import {
  IntegrationSuggestionSurfaces,
  OnboardingWizardEvents,
  trackEvent
} from '../../analytics/posthog'

// Fills the 4x4 grid exactly. A seventeenth suggestion would be a scroll, and a
// scroll on the last step before "Ready!" is where people leave.
const ONBOARDING_LIMIT = 16
// There is deliberately no bulk action here, and no pre-selection.
//
// The step used to be subtractive: sixteen tiles you removed the wrong ones
// from. That asked a brand-new user to curate a list of apps we guessed at
// before they could move on — work with no reward, where the only available
// action was rejection and doing nothing was the fastest path out. It also made
// the destructive choice the cheap one: dismissal survives every rescan, and an
// earlier revision went further still and dismissed everything left unselected
// on continue.
//
// Now the only action is connecting something you actually use. Anything left
// alone is untouched — not dismissed — and waits in Settings.

// The scan is kicked off from the OAuth callback, so by the time a user reaches
// this step it has almost always finished. These bound the case where it hasn't:
// poll briefly, then move on rather than hold the wizard.
const POLL_INTERVAL_MS = 1200
const MAX_POLLS = 8

interface ToolsStepProps {
  onContinue: () => void
}

export function ToolsStep({ onContinue }: ToolsStepProps): JSX.Element {
  const [suggestions, setSuggestions] = useState<IntegrationSuggestionItem[]>([])
  const [connectedKeys, setConnectedKeys] = useState<Set<string>>(new Set())
  const [scanning, setScanning] = useState(true)
  const pollCount = useRef(0)
  const cancelled = useRef(false)
  // Guards the auto-skip's terminal event against a second emission. The effect
  // that fires it depends on `onContinue`, which is not guaranteed to be
  // referentially stable, so it can re-run before the wizard has swapped the
  // step out from under it.
  const skipTracked = useRef(false)

  // A connected app drops out of the server's shortlist, so "is it gone from a
  // fresh read?" is the one connection check that works identically for all
  // four connect backends — no per-backend status endpoint, and no trusting a
  // postMessage that some flows never send. The tile stays on screen either
  // way: this grid is the user's working surface for the length of the step,
  // and having a tile vanish under the cursor mid-grid is its own bug.
  // Returns what the server still suggests, so the caller below can answer the
  // one question this read was going to answer anyway — is the clicked tile
  // gone? — without paying for a second read. `null` means the read failed and
  // we genuinely do not know, which is a different answer from "not connected".
  //
  // A settle that races the step being left also lands on `null`, via the
  // `cancelled` guard, and so reports `unverified` rather than `left_page`.
  // Both are abandonment and neither is counted as intent, so the distinction
  // does not move any read that matters — but `unverified` is documented as our
  // outage, and this is the one way it fires without one.
  const reconcile = useCallback(async (): Promise<Set<string> | null> => {
    try {
      const result = await integrationSuggestionsService.list(ONBOARDING_LIMIT)
      if (cancelled.current) return null
      const stillSuggested = new Set(result.suggestions.map((s) => s.catalog_key))
      // Reconcile every tile against this response, not just the one that was
      // clicked. The read costs the same either way — the server re-checks the
      // connection state of all sixteen rows to answer it — so narrowing it to
      // one key would throw away fifteen answers we already paid for and make
      // the next connect pay for them again. It also picks up anything
      // connected in another tab.
      setConnectedKeys((current) => {
        const next = new Set(current)
        for (const shown of suggestions) {
          if (!stillSuggested.has(shown.catalog_key)) next.add(shown.catalog_key)
        }
        return next
      })
      return stillSuggested
    } catch {
      // Leave the tile connectable. Showing an unverified tick would tell the
      // user their tool is wired up when it may not be, and the cost of being
      // wrong here is discovering it the first time the assistant can't act.
      return null
    }
  }, [suggestions])

  const { connectingKey, error, connect } = useSuggestionConnect({
    surface: IntegrationSuggestionSurfaces.ONBOARDING,
    onSettled: async (suggestion) =>
      verdictFromShortlist(await reconcile(), suggestion.catalog_key),
  })

  useEffect(() => {
    cancelled.current = false
    let timer: number | undefined

    const poll = async (): Promise<void> => {
      try {
        const result = await integrationSuggestionsService.list(ONBOARDING_LIMIT)
        if (cancelled.current) return
        // `scan_complete` is what separates "we looked and found nothing" from
        // "we haven't looked yet". Only the first is a reason to stop waiting.
        if (result.scan_complete || pollCount.current >= MAX_POLLS) {
          setSuggestions(result.suggestions)
          setScanning(false)
          trackEvent(OnboardingWizardEvents.TOOLS_STEP_VIEWED, {
            suggestion_count: result.suggestions.length,
            has_more: result.has_more,
            scan_complete: result.scan_complete,
            // Whether a grid actually reached the screen. An empty shortlist
            // auto-continues below without rendering anything, so this event
            // means "the step resolved", not "the user saw it" — and reading it
            // as the latter blames the step for the scan coming back empty.
            rendered: result.suggestions.length > 0,
            load_failed: false,
          })
          return
        }
      } catch {
        // A failed read is not a reason to trap the user on this step. Fall
        // through to the empty state, which auto-continues below.
        if (cancelled.current) return
        setScanning(false)
        // Counted like any other resolution: this is a step the user reached
        // and left. Emitting nothing here would leave the skip event below with
        // no matching view, which is the same reconciliation hole in reverse —
        // and would hide a failing endpoint inside the empty-shortlist bucket.
        trackEvent(OnboardingWizardEvents.TOOLS_STEP_VIEWED, {
          suggestion_count: 0,
          has_more: false,
          scan_complete: false,
          rendered: false,
          load_failed: true,
        })
        return
      }
      pollCount.current += 1
      timer = window.setTimeout(() => void poll(), POLL_INTERVAL_MS)
    }

    void poll()
    return () => {
      cancelled.current = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [])

  // Nothing to suggest is not a step — it's a blank screen with a Skip button.
  // Skipping past it keeps the wizard honest about only showing steps that have
  // something to say.
  //
  // The skip still emits COMPLETED, so an auto-skipped VIEWED has a matching
  // terminal event. Before this it emitted nothing, and an auto-skipped step was
  // indistinguishable from a user who reached the grid and walked away: 4 of the
  // 10 views in the 24h to 2026-08-11 were skips, dragging the apparent
  // completion rate to 40% against a true 4-of-6 (GET-11823). A rendered step
  // that is abandoned still has no COMPLETED, and should not — that absence is
  // the real drop-off. Zero counts rather
  // than the live state because the skip happens before either can be non-zero,
  // and `skipped` is what keeps the two populations separable instead of
  // silently averaging a step nobody saw into the one they did.
  useEffect(() => {
    if (scanning || suggestions.length > 0) return
    if (!skipTracked.current) {
      skipTracked.current = true
      trackEvent(OnboardingWizardEvents.TOOLS_STEP_COMPLETED, {
        shown_count: 0,
        connected_count: 0,
        skipped: true,
      })
    }
    onContinue()
  }, [scanning, suggestions.length, onContinue])

  // The click event is the hook's now (GET-13089). Every tap here reaches
  // `connect`, and only `connect` knows whether it was accepted — a tap made
  // while another popup is still open is refused, and counting it would file a
  // click that no terminal event can ever close.
  const handleConnect = useCallback(
    (suggestion: IntegrationSuggestionItem) => void connect(suggestion),
    [connect],
  )

  const handleContinue = useCallback(() => {
    // `kept_count` is gone with the model that produced it: under a subtractive
    // grid it counted what survived, which under an additive one would just be
    // "everything we showed minus what they connected" — a restatement of the
    // two numbers below, and one that reads like a rejection metric when no
    // rejection happened.
    trackEvent(OnboardingWizardEvents.TOOLS_STEP_COMPLETED, {
      shown_count: suggestions.length,
      connected_count: connectedKeys.size,
      skipped: false,
    })
    onContinue()
  }, [suggestions.length, connectedKeys.size, onContinue])

  if (scanning) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-50 mb-5">
          <Loader2 className="w-7 h-7 text-primary-500 animate-spin" />
        </div>
        <h2 className="text-2xl font-display uppercase tracking-tight text-gray-900 mb-2">
          Finding the tools you already use
        </h2>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          We&apos;re reading who has emailed you — sender addresses only.
          <span className="text-gray-400"> Not a single subject line or message body.</span>
        </p>
      </div>
    )
  }

  if (suggestions.length === 0) return <div />

  const connectedCount = connectedKeys.size

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-primary-500" />
        <h2 className="text-2xl font-display uppercase tracking-tight text-gray-900">These look like your tools</h2>
      </div>
      {/* Describes the mechanic it actually has: each tile connects on tap, one
          at a time. An earlier draft ("pick the ones you want and we'll connect
          them now") implied a batch you assemble and submit, which would make
          the first popup a surprise. */}
      <p className="text-sm text-gray-500 mb-6 max-w-lg">
        Tap the ones you&apos;d like your assistant to work in. We&apos;ll
        connect each as you go — anything you skip is waiting for you in
        Settings.
      </p>

      {error !== null && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {suggestions.map((suggestion) => {
          const isConnected = connectedKeys.has(suggestion.catalog_key)
          // A discovered-OAuth app can't start without a resource URL only the
          // user has, so it renders as a preview rather than a button that
          // would fail on tap. It is still a useful thing to have seen — the
          // subtitle's promise about Settings is where it gets picked up.
          const connectable = isInlineConnectable(suggestion)
          return (
            <SuggestedIntegrationTile
              key={suggestion.catalog_key}
              suggestion={suggestion}
              loading={connectingKey === suggestion.catalog_key}
              connected={isConnected}
              {...(connectable
                ? { onClick: () => handleConnect(suggestion) }
                : {})}
            />
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-7 pt-5 border-t border-gray-100">
        <button
          type="button"
          onClick={handleContinue}
          className="px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors"
        >
          {connectedCount > 0 ? 'Continue' : 'Skip for now'}
        </button>
        <span className="text-xs text-gray-400">
          {connectedCount > 0
            ? `${connectedCount} connected. You can add the rest any time in Settings.`
            : 'You can connect these any time in Settings.'}
        </span>
      </div>
    </div>
  )
}
